/* Illustrations SVG pour les exercices — style stick-figure teal, fond blanc.
   Toutes les illustrations partagent un viewBox 200x200 et utilisent #2BA3A0 + #D8D3C4. */
import { memo } from "react";

const TEAL = "#2BA3A0";
const TEAL_DARK = "#0A1426";
const FLOOR = "#D8D3C4";
const FILL_SOFT = "#DCEEEA";

const Frame = ({ children, badge }) => (
  <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    {children}
    {badge != null && (
      <g>
        <circle cx="22" cy="22" r="14" fill={TEAL} />
        <text
          x="22" y="22" textAnchor="middle" dominantBaseline="central"
          fontFamily="Manrope, sans-serif" fontWeight="700"
          fontSize="14" fill="#fff"
        >{badge}</text>
      </g>
    )}
  </svg>
);

/* ---------- Stick figure helpers ---------- */
const Chair = ({ x = 60 }) => (
  <g stroke="#9FB7B2" strokeWidth="4" fill="none" strokeLinecap="round">
    <line x1={x} y1="80" x2={x} y2="170" />
    <line x1={x + 50} y1="120" x2={x + 50} y2="170" />
    <line x1={x} y1="120" x2={x + 50} y2="120" />
  </g>
);

const Floor = () => (
  <rect x="10" y="172" width="180" height="6" rx="3" fill={FLOOR} />
);

/* ---------- Illustrations ---------- */

const SitUpright = ({ badge }) => (
  <Frame badge={badge}>
    <Floor />
    <Chair x={70} />
    {/* Head */}
    <circle cx="110" cy="58" r="14" fill={TEAL} />
    {/* Torso */}
    <path d="M110 72 L110 120" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    {/* Arms folded */}
    <path d="M110 92 Q92 100 96 116" stroke={TEAL} strokeWidth="8" strokeLinecap="round" fill="none" />
    {/* Legs sitting */}
    <path d="M110 120 L150 120" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M150 120 L150 168" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
  </Frame>
);

const SitDrink = ({ badge }) => (
  <Frame badge={badge}>
    <Floor />
    <Chair x={70} />
    <circle cx="110" cy="58" r="14" fill={TEAL} />
    <path d="M110 72 L110 118" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    {/* Arm bringing cup to mouth */}
    <path d="M110 84 Q124 70 132 64" stroke={TEAL} strokeWidth="8" strokeLinecap="round" fill="none" />
    {/* Cup */}
    <rect x="124" y="50" width="14" height="14" rx="2" fill={TEAL_DARK} />
    {/* Legs */}
    <path d="M110 118 L148 118" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M148 118 L148 168" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
  </Frame>
);

const SitButton = ({ badge }) => (
  <Frame badge={badge}>
    <Floor />
    <Chair x={70} />
    <circle cx="110" cy="58" r="14" fill={TEAL} />
    <path d="M110 72 L110 120" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    {/* Both hands meeting on torso */}
    <path d="M110 92 Q126 96 122 110" stroke={TEAL} strokeWidth="8" strokeLinecap="round" fill="none" />
    <path d="M110 92 Q94 96 98 110" stroke={TEAL} strokeWidth="8" strokeLinecap="round" fill="none" />
    {/* Buttons on torso */}
    <circle cx="110" cy="96" r="2" fill="#fff" />
    <circle cx="110" cy="104" r="2" fill="#fff" />
    <circle cx="110" cy="112" r="2" fill="#fff" />
    {/* Legs */}
    <path d="M110 120 L150 120" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M150 120 L150 168" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
  </Frame>
);

const StandUp = ({ badge }) => (
  <Frame badge={badge}>
    <Floor />
    <Chair x={50} />
    {/* Standing figure */}
    <circle cx="130" cy="50" r="14" fill={TEAL} />
    <path d="M130 64 L130 120" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M130 84 L112 100" stroke={TEAL} strokeWidth="8" strokeLinecap="round" />
    <path d="M130 84 L148 100" stroke={TEAL} strokeWidth="8" strokeLinecap="round" />
    <path d="M130 120 L122 168" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M130 120 L142 168" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    {/* Up arrow */}
    <path d="M170 130 L170 80 M160 92 L170 80 L180 92" stroke={TEAL_DARK} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </Frame>
);

const SitOnEdge = ({ badge }) => (
  <Frame badge={badge}>
    <Floor />
    <Chair x={50} />
    {/* Sitting on edge */}
    <circle cx="105" cy="60" r="14" fill={TEAL} />
    <path d="M105 74 L105 122" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M105 94 L88 110" stroke={TEAL} strokeWidth="8" strokeLinecap="round" />
    <path d="M105 94 L122 110" stroke={TEAL} strokeWidth="8" strokeLinecap="round" />
    <path d="M105 122 L130 140 L130 168" stroke={TEAL} strokeWidth="10" strokeLinecap="round" fill="none" />
  </Frame>
);

const Walk = ({ badge }) => (
  <Frame badge={badge}>
    <Floor />
    <circle cx="100" cy="50" r="14" fill={TEAL} />
    <path d="M100 64 L100 116" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    {/* Arms swinging */}
    <path d="M100 80 L82 100" stroke={TEAL} strokeWidth="8" strokeLinecap="round" />
    <path d="M100 80 L120 102" stroke={TEAL} strokeWidth="8" strokeLinecap="round" />
    {/* Legs walking */}
    <path d="M100 116 L82 168" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M100 116 L122 168" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    {/* Motion lines */}
    <path d="M40 80 L60 80 M44 96 L60 96 M40 112 L60 112" stroke={TEAL} strokeOpacity="0.5" strokeWidth="3" strokeLinecap="round" />
  </Frame>
);

const BalanceOneLeg = ({ badge }) => (
  <Frame badge={badge}>
    <Floor />
    <circle cx="100" cy="46" r="14" fill={TEAL} />
    <path d="M100 60 L100 116" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M100 80 L80 96" stroke={TEAL} strokeWidth="8" strokeLinecap="round" />
    <path d="M100 80 L122 96" stroke={TEAL} strokeWidth="8" strokeLinecap="round" />
    {/* One leg down */}
    <path d="M100 116 L100 168" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    {/* Other leg lifted */}
    <path d="M100 116 L130 140" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    {/* Balance wave */}
    <path d="M150 90 Q160 80 170 90 T190 90" stroke={TEAL_DARK} strokeWidth="3" fill="none" strokeLinecap="round" />
  </Frame>
);

const HandOpen = ({ badge }) => (
  <Frame badge={badge}>
    {/* Hand silhouette */}
    <g fill="none" stroke={TEAL} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M70 130 L70 90 Q70 78 82 78 Q94 78 94 90 L94 70 Q94 58 106 58 Q118 58 118 70 L118 88 Q118 60 130 60 Q142 60 142 72 L142 96 Q142 70 154 70 Q166 70 166 82 L166 130 Q166 168 118 168 Q70 168 70 130 Z" fill={FILL_SOFT} />
    </g>
  </Frame>
);

const HandClose = ({ badge }) => (
  <Frame badge={badge}>
    {/* Fist */}
    <g fill={FILL_SOFT} stroke={TEAL} strokeWidth="8" strokeLinejoin="round" strokeLinecap="round">
      <path d="M70 122 Q70 92 100 92 L150 92 Q170 92 170 110 L170 132 Q170 168 118 168 Q70 168 70 132 Z" />
      <path d="M88 92 Q88 78 100 78 Q112 78 112 92" fill="none" />
      <path d="M112 92 Q112 74 124 74 Q136 74 136 92" fill="none" />
      <path d="M136 92 Q136 80 148 80 Q160 80 160 92" fill="none" />
    </g>
  </Frame>
);

const FourObjects = ({ badge }) => (
  <Frame badge={badge}>
    {/* Table */}
    <rect x="20" y="120" width="160" height="8" rx="2" fill={FLOOR} />
    {/* 4 objects */}
    <circle cx="50" cy="100" r="14" fill={TEAL} />
    <rect x="78" y="86" width="28" height="28" rx="4" fill={TEAL_DARK} />
    <polygon points="135,86 153,116 117,116" fill={TEAL} />
    <rect x="160" y="88" width="20" height="28" rx="6" fill={TEAL_DARK} />
    {/* Eye looking */}
    <ellipse cx="100" cy="50" rx="22" ry="12" fill="none" stroke={TEAL} strokeWidth="3" />
    <circle cx="100" cy="50" r="6" fill={TEAL} />
  </Frame>
);

const EyesClosed = ({ badge }) => (
  <Frame badge={badge}>
    {/* Face */}
    <circle cx="100" cy="100" r="60" fill={FILL_SOFT} stroke={TEAL} strokeWidth="4" />
    <path d="M70 92 Q80 100 90 92" stroke={TEAL} strokeWidth="4" fill="none" strokeLinecap="round" />
    <path d="M110 92 Q120 100 130 92" stroke={TEAL} strokeWidth="4" fill="none" strokeLinecap="round" />
    <path d="M82 128 Q100 134 118 128" stroke={TEAL_DARK} strokeWidth="3" fill="none" strokeLinecap="round" />
    {/* Z's */}
    <text x="158" y="64" fontFamily="Manrope" fontSize="22" fontWeight="700" fill={TEAL}>z</text>
    <text x="172" y="48" fontFamily="Manrope" fontSize="16" fontWeight="700" fill={TEAL_DARK}>z</text>
  </Frame>
);

const PourPitcher = ({ badge }) => (
  <Frame badge={badge}>
    <Floor />
    {/* Pitcher */}
    <path d="M50 80 L50 130 Q50 150 70 150 L90 150 Q110 150 110 130 L110 80 Z" fill={TEAL} />
    <path d="M110 95 Q130 95 130 110 Q130 120 110 120" fill="none" stroke={TEAL} strokeWidth="6" />
    {/* Stream */}
    <path d="M120 130 Q140 145 145 165" stroke={TEAL} strokeWidth="4" fill="none" strokeLinecap="round" />
    {/* Glass */}
    <path d="M132 160 L158 160 L154 178 L136 178 Z" fill="none" stroke={TEAL_DARK} strokeWidth="3" />
  </Frame>
);

const Repeat = ({ badge, times = 5 }) => (
  <Frame badge={badge}>
    {/* circular arrow */}
    <path
      d="M60 100 A40 40 0 1 1 100 140"
      fill="none" stroke={TEAL} strokeWidth="10" strokeLinecap="round"
    />
    <polygon points="92,128 116,140 100,152" fill={TEAL} />
    <text
      x="100" y="98" textAnchor="middle" dominantBaseline="central"
      fontFamily="Manrope" fontWeight="800" fontSize="44" fill={TEAL_DARK}
    >×{times}</text>
  </Frame>
);

const Celebrate = ({ badge }) => (
  <Frame badge={badge}>
    <circle cx="100" cy="80" r="22" fill={TEAL} />
    {/* Smile */}
    <path d="M88 84 Q100 92 112 84" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round" />
    {/* Arms up */}
    <path d="M100 100 L60 60" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M100 100 L140 60" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M100 100 L100 150" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M100 150 L82 178" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    <path d="M100 150 L118 178" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
    {/* Sparkles */}
    <g fill={TEAL_DARK}>
      <circle cx="40" cy="40" r="3" />
      <circle cx="170" cy="50" r="3" />
      <circle cx="160" cy="120" r="3" />
      <circle cx="30" cy="120" r="3" />
    </g>
  </Frame>
);

const MAP = {
  "sit-upright": SitUpright,
  "sit-drink": SitDrink,
  "sit-button": SitButton,
  "stand-up": StandUp,
  "sit-edge": SitOnEdge,
  walk: Walk,
  balance: BalanceOneLeg,
  "hand-open": HandOpen,
  "hand-close": HandClose,
  "four-objects": FourObjects,
  "eyes-closed": EyesClosed,
  "pour-pitcher": PourPitcher,
  repeat: Repeat,
  celebrate: Celebrate,
};

export const Illustration = memo(function Illustration({ name, badge, times }) {
  const Comp = MAP[name] || SitUpright;
  return <Comp badge={badge} times={times} />;
});

export default Illustration;
